/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ParentalControlState } from "../enums";
import { Log } from "../logging/Log";
import type {
  TContentRating,
  TParentalFeature,
  TParentalFeatureAttribute,
  TParentalSettings
} from "../types";
import { toContentRatings } from "./contentRatingMapper";

/**
 * Parses the `parentalSettings` device feature from `GET /v2/deviceFeatures`
 * into {@link TParentalSettings}.
 *
 * The sources disagree on attribute names and value shapes (Web Client DRD
 * section 4.2), so every documented variant is accepted. Missing or
 * malformed values resolve to the most restrictive setting.
 */
export class ParentalSettingsParser {
  private static readonly TAG: string = "ParentalSettingsParser";

  /** FE feature name. */
  public static readonly FEATURE_NAME: string = "parentalSettings";

  /** Attribute names for the PC state. */
  private static readonly STATE_NAMES: ReadonlyArray<string> = [
    "state",
    "parentalSettings-state"
  ];

  /** Attribute names for Hide Adult Content. */
  private static readonly HIDE_ADULT_NAMES: ReadonlyArray<string> = [
    "hideAdult",
    "parentalSettings-hideAdult"
  ];

  /** Attribute names for the PIN hash (`hashedPin` in BE/ONB, `pin` in API/UPC). */
  private static readonly PIN_NAMES: ReadonlyArray<string> = [
    "hashedPin",
    "parentalSettings-hashedPin",
    "pin",
    "parentalSettings-pin"
  ];

  /** Attribute names for rating limits. */
  private static readonly RATING_LIMITS_NAMES: ReadonlyArray<string> = [
    "ratingLimits",
    "parentalSettings-ratingLimits"
  ];

  /** Attribute names for the allow-unrated flag (BE/ONB). */
  private static readonly ALLOW_UNRATED_NAMES: ReadonlyArray<string> = [
    "allowUnrated",
    "parentalSettings-allowUnrated"
  ];

  /** Attribute names for the block-unrated flag (UPC wording; inverted). */
  private static readonly BLOCK_UNRATED_NAMES: ReadonlyArray<string> = [
    "blockUnratedContent",
    "parentalSettings-blockUnratedContent"
  ];

  /**
   * Find the first attribute whose name matches any candidate.
   * @param attributes - Feature attributes
   * @param names - Candidate attribute names
   * @returns The raw value, or undefined when absent
   */
  private static findValue(
    attributes: ReadonlyArray<TParentalFeatureAttribute>,
    names: ReadonlyArray<string>
  ): unknown {
    for (const name of names) {
      const attribute: TParentalFeatureAttribute | undefined = attributes.find(
        (item: TParentalFeatureAttribute): boolean =>
          item !== null && item !== undefined && item.attributeName === name
      );
      if (attribute !== undefined) {
        return attribute.attributeValue;
      }
    }
    return undefined;
  }

  /**
   * Parse a boolean that may arrive as a boolean or a string.
   * @param value - Raw value
   * @returns The boolean, or null when absent or unrecognised
   */
  private static parseBoolean(value: unknown): boolean | null {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value !== "string") {
      return null;
    }
    const normalized: string = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
    return null;
  }

  /**
   * Parse the PC state. `On`/`ON`/`on` and `Off`/`OFF`/`off` are accepted.
   * @param value - Raw value
   * @returns The state; ON when absent or unrecognised (fail closed)
   */
  private static parseState(value: unknown): ParentalControlState {
    if (typeof value === "string") {
      const normalized: string = value.trim().toUpperCase();
      if (normalized === ParentalControlState.OFF) {
        return ParentalControlState.OFF;
      }
      if (normalized === ParentalControlState.ON) {
        return ParentalControlState.ON;
      }
    }
    Log.w(
      ParentalSettingsParser.TAG,
      "parseState() — missing or unknown state, treating as ON"
    );
    return ParentalControlState.ON;
  }

  /**
   * Parse rating limits. Accepts a serialized JSON array (ONB format) or an
   * array. A bare `ratingValueId` string (API contract wording) carries no
   * rating type and cannot be used, so it yields no limits.
   * @param value - Raw value
   * @returns Rating limits; empty when absent or unusable
   */
  private static parseRatingLimits(
    value: unknown
  ): ReadonlyArray<TContentRating> {
    let candidate: unknown = value;
    if (typeof value === "string") {
      try {
        candidate = JSON.parse(value);
      } catch {
        Log.w(
          ParentalSettingsParser.TAG,
          "parseRatingLimits() — value is not JSON; no limits applied"
        );
        return [];
      }
    }
    const limits: ReadonlyArray<TContentRating> = toContentRatings(candidate);
    if (limits.length === 0) {
      Log.w(ParentalSettingsParser.TAG, "parseRatingLimits() — no limits");
    }
    return limits;
  }

  /**
   * Parse the `parentalSettings` feature.
   * @param feature - The feature, or null/undefined when FE did not return it
   * @returns Settings, or null when the feature or its attributes are missing
   */
  public static parse(
    feature: TParentalFeature | null | undefined
  ): TParentalSettings | null {
    if (
      feature === null ||
      feature === undefined ||
      !Array.isArray(feature.attributes)
    ) {
      Log.w(ParentalSettingsParser.TAG, "parse() — feature missing");
      return null;
    }
    const attributes: ReadonlyArray<TParentalFeatureAttribute> =
      feature.attributes;

    const hideAdult: boolean | null = ParentalSettingsParser.parseBoolean(
      ParentalSettingsParser.findValue(
        attributes,
        ParentalSettingsParser.HIDE_ADULT_NAMES
      )
    );

    let allowUnrated: boolean | null = ParentalSettingsParser.parseBoolean(
      ParentalSettingsParser.findValue(
        attributes,
        ParentalSettingsParser.ALLOW_UNRATED_NAMES
      )
    );
    if (allowUnrated === null) {
      const blockUnrated: boolean | null = ParentalSettingsParser.parseBoolean(
        ParentalSettingsParser.findValue(
          attributes,
          ParentalSettingsParser.BLOCK_UNRATED_NAMES
        )
      );
      allowUnrated = blockUnrated === null ? null : !blockUnrated;
    }

    const rawPin: unknown = ParentalSettingsParser.findValue(
      attributes,
      ParentalSettingsParser.PIN_NAMES
    );

    return {
      allowUnrated,
      hideAdult: hideAdult === null ? true : hideAdult,
      pinHash:
        typeof rawPin === "string" && rawPin.trim() !== ""
          ? rawPin.trim()
          : null,
      ratingLimits: ParentalSettingsParser.parseRatingLimits(
        ParentalSettingsParser.findValue(
          attributes,
          ParentalSettingsParser.RATING_LIMITS_NAMES
        )
      ),
      state: ParentalSettingsParser.parseState(
        ParentalSettingsParser.findValue(
          attributes,
          ParentalSettingsParser.STATE_NAMES
        )
      )
    };
  }
}
